/**
 * Édition d'une recette (specs §6.5, §4.3).
 *
 * 🔴 **Aucun champ de macro dans ce formulaire, et c'est le point cardinal du
 * §6.5.** Les macros ne sont pas saisissables : elles se déduisent des
 * ingrédients, s'affichent en lecture seule et se mettent à jour à la frappe. Ce
 * n'est pas un choix d'ergonomie, c'est la règle — « macros toujours calculées
 * depuis les ingrédients, jamais saisies ».
 *
 * ⚠️ Le total montré ici n'est qu'un **aperçu** (`lib/recipePreview.ts`) : le
 * serveur le recalcule à chaque lecture, et c'est sa réponse qui fait foi (§5.1).
 *
 * ⚠️ Le sélecteur d'ingrédient est **en ligne**, jamais une seconde feuille
 * modale : deux feuilles empilées seraient une impasse gestuelle sur iPhone —
 * deux poignées de glissement superposées, deux voiles. Même règle qu'à l'écran
 * d'ajout, où le panneau de quantité remplace la liste dans la même feuille.
 *
 * ⚠️ Enregistrer une recette **n'a aucun effet sur le journal déjà saisi** (§4.1).
 * L'écran le rappelle, parce que c'est contre-intuitif dans les deux sens : la
 * correction se répercute bien sur la *recette*, jamais sur l'*historique*.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import type { FoodDTO, MacroSet } from '../../../../shared/types';
import { fetchFoods } from '../../api/foods';
import { Button, GlyphClose } from '../../components/nourri';
import { Field, Hint, NumberField, Sheet } from '../../components/ui/primitives';
import { MacroDot } from '../../components/ui/MacroMeter';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { newClientId } from '../../lib/dates';
import { previewMacrosFor } from '../../lib/foodPreview';
import { toNumber } from '../../lib/formFields';
import { formatMacro, MACRO_ROWS } from '../../lib/macros';
import { divideMacros, sumMacros } from '../../lib/recipePreview';
import { isRecipeDraftValid, type IngredientDraft, type RecipeDraft } from './recipeDraft';

/** Quantité en unité de base d'un ingrédient en cours de saisie. */
function baseQuantityOfDraft(item: IngredientDraft): number {
  const typed = Math.max(0, toNumber(item.quantity));
  if (item.portionId === null) return typed;
  const portion = item.food.portions.find((candidate) => candidate.id === item.portionId);
  return portion === undefined ? typed : typed * portion.quantity;
}

/** Macros d'un ingrédient en cours de saisie — aperçu, cf. en-tête. */
function macrosOfDraft(item: IngredientDraft): MacroSet {
  return previewMacrosFor(item.food.per100, baseQuantityOfDraft(item));
}

/* ==========================================================================
 * Sélecteur d'ingrédient — en ligne, pas de seconde feuille
 * ======================================================================= */

function FoodPicker({ onPick, onCancel }: { onPick: (food: FoodDTO) => void; onCancel: () => void }) {
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term.trim());

  const foods = useQuery({
    queryKey: ['foods', debounced],
    queryFn: () => fetchFoods(debounced),
  });

  return (
    <div className="n-panel n-panel--active" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div className="n-row">
        <span className="n-field__label" style={{ flex: 1 }}>
          Choisir un aliment de ma base
        </span>
        <Button variant="secondary" shape="rounded" onClick={onCancel}>
          Annuler
        </Button>
      </div>

      <input
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        placeholder="Chercher…"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
        // ⚠️ 16 px minimum : `controls.css` le pose sur tous les contrôles.
        className="ui-input"
      />

      {foods.isPending && <Hint>Chargement…</Hint>}
      {foods.data?.length === 0 && (
        <Hint>
          Aucun aliment ne correspond. Les ingrédients viennent de la base personnelle : créer la
          fiche d’abord, dans « Aliments » juste au-dessus.
        </Hint>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '14rem', overflowY: 'auto' }}>
        {(foods.data ?? []).map((food) => (
          <button
            key={food.id}
            type="button"
            onClick={() => {
              onPick(food);
            }}
            className="n-pick"
          >
            <span className="n-pick__main">
            <span className="n-pick__name n-ellipsis">
              {food.name}
              {food.brand !== null && <span className="n-list__brand"> · {food.brand}</span>}
            </span>
            <span className="n-pick__meta tnum">
              100 {food.baseUnit} : {formatMacro(food.per100.protein, 'g')} P ·{' '}
              {formatMacro(food.per100.carbs, 'g')} G · {formatMacro(food.per100.fat, 'g')} L ·{' '}
              {formatMacro(food.per100.kcal, 'kcal')} kcal
            </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
 * Une ligne d'ingrédient
 * ======================================================================= */

function IngredientRow({
  item,
  onPatch,
  onRemove,
}: {
  item: IngredientDraft;
  onPatch: (patch: Partial<IngredientDraft>) => void;
  onRemove: () => void;
}) {
  const macros = macrosOfDraft(item);
  const baseQuantity = baseQuantityOfDraft(item);

  // Unité de base + portions nommées, comme à l'écran de quantité du journal.
  // ⚠️ Masqué quand l'aliment n'a aucune portion : un choix à une seule option
  // n'est pas un choix, et le champ annonce déjà son unité.
  const modes = [
    { id: null as number | null, label: item.food.baseUnit },
    ...item.food.portions.map((portion) => ({
      id: portion.id,
      label: `${portion.label} · ${String(portion.quantity)} ${item.food.baseUnit}`,
    })),
  ];

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: '0.75rem',
        padding: '0.6rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.4rem',
      }}
    >
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: '0.95rem',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.food.name}
          {item.food.brand !== null && (
            <span className="n-list__brand"> · {item.food.brand}</span>
          )}
        </span>
        <Button
          variant="danger"
          shape="circle"
          aria-label={`Retirer ${item.food.name}`}
          onClick={onRemove}
        >
          <GlyphClose />
        </Button>
      </div>

      {modes.length > 1 && (
        <div className="n-chip-row">
          {modes.map((mode) => (
            <Button
              key={String(mode.id)}
              aria-pressed={item.portionId === mode.id}
              variant={item.portionId === mode.id ? 'selected' : 'secondary'}
              onClick={() => {
                onPatch({ portionId: mode.id, quantity: mode.id === null ? '100' : '1' });
              }}
            >
              {mode.label}
            </Button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <NumberField
            label={item.portionId === null ? `Quantité (${item.food.baseUnit})` : 'Nombre de portions'}
            value={item.quantity}
            onChange={(quantity) => {
              onPatch({ quantity });
            }}
          />
        </div>
        <span
          className="n-pick__meta tnum"
          style={{ paddingBottom: '0.7rem', whiteSpace: 'nowrap' }}
        >
          {item.portionId !== null && `${formatMacro(baseQuantity, item.food.baseUnit)} ${item.food.baseUnit} · `}
          {formatMacro(macros.kcal, 'kcal')} kcal
        </span>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Éditeur
 * ======================================================================= */

function TagEditor({ tags, onChange }: { tags: string[]; onChange: (next: string[]) => void }) {
  const [entry, setEntry] = useState('');

  const add = () => {
    const value = entry.trim();
    if (value === '') return;
    // Comparaison sans casse ni accents, comme le serveur : « Végé » et « vege »
    // ne doivent pas faire deux chips.
    const fold = (text: string) =>
      text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (!tags.some((tag) => fold(tag) === fold(value))) onChange([...tags, value]);
    setEntry('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <span className="n-field__label">Tags</span>

      {tags.length > 0 && (
        <div className="n-chip-row">
          {tags.map((tag) => (
            <Button
              key={tag}
              variant="selected"
              onClick={() => {
                onChange(tags.filter((item) => item !== tag));
              }}
              aria-label={`Retirer le tag ${tag}`}
            >
              {tag} ✕
            </Button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input
          value={entry}
          onChange={(event) => {
            setEntry(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            // Sans cela, Entrée valide le formulaire entier au lieu d'ajouter
            // le tag qu'on vient de taper.
            event.preventDefault();
            add();
          }}
          placeholder="végé, rapide, batch…"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          className="ui-input"
          style={{ flex: 1 }}
        />
        <Button variant="secondary" shape="rounded" onClick={add}>
          Ajouter
        </Button>
      </div>
    </div>
  );
}

export function RecipeEditor({
  open,
  title,
  draft,
  onDraftChange,
  onSubmit,
  onClose,
  saving,
}: {
  open: boolean;
  title: string;
  draft: RecipeDraft;
  onDraftChange: (next: RecipeDraft) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const [picking, setPicking] = useState(false);

  const totals = useMemo(
    () => sumMacros(draft.ingredients.map(macrosOfDraft)),
    [draft.ingredients],
  );
  const yieldValue = toNumber(draft.yieldValue);
  const isPortions = draft.yieldType === 'portions';
  const perUnit = isPortions
    ? divideMacros(totals, yieldValue)
    : divideMacros(totals, yieldValue, 100);
  const ingredientsQuantity = draft.ingredients.reduce(
    (total, item) => total + baseQuantityOfDraft(item),
    0,
  );

  const set = <K extends keyof RecipeDraft>(key: K) => (value: RecipeDraft[K]) => {
    onDraftChange({ ...draft, [key]: value });
  };

  return (
    <Sheet open={open} title={title} onClose={onClose}>
      <Field label="Nom" value={draft.name} onChange={set('name')} placeholder="Curry de pois chiches" />

      <TagEditor tags={draft.tags} onChange={set('tags')} />

      {/* Rendement — « au choix en portions ou en poids total » (specs §5.1). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <span className="n-field__label">Rendement</span>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {([
            { type: 'portions' as const, label: 'En portions' },
            { type: 'weight' as const, label: 'En poids total' },
          ]).map((option) => (
            <Button
              key={option.type}
              aria-pressed={draft.yieldType === option.type}
              onClick={() => {
                onDraftChange({
                  ...draft,
                  yieldType: option.type,
                  // Repartir d'une valeur plausible : « 4 » n'a aucun sens en
                  // grammes, et « 1200 » n'en a aucun en parts.
                  yieldValue:
                    option.type === 'portions'
                      ? '4'
                      : String(Math.round(ingredientsQuantity) || 1000),
                });
              }}
              variant={draft.yieldType === option.type ? 'selected' : 'secondary'}
              shape="rounded"
            >
              {option.label}
            </Button>
          ))}
        </div>
        <NumberField
          label={isPortions ? 'Nombre de portions' : 'Poids du plat fini'}
          suffix={isPortions ? undefined : 'g'}
          value={draft.yieldValue}
          onChange={set('yieldValue')}
        />
        {!isPortions && ingredientsQuantity > 0 && (
          <Hint>
            Somme des ingrédients : {formatMacro(ingredientsQuantity, 'g')} g. Le poids fini est
            souvent plus faible — un plat perd de l’eau à la cuisson, et c’est bien le poids fini
            qui sert à ramener à 100 g.
          </Hint>
        )}
      </div>

      {/*
        Total en lecture seule : il se déduit des ingrédients, il ne se saisit
        pas (specs §6.5). Placé au-dessus de la liste pour rester sous les yeux
        pendant qu'on règle les quantités — même disposition qu'à l'éditeur de
        modèle de journée, où le total suit la même logique.
      */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4rem',
          padding: '0.6rem',
          borderRadius: '0.75rem',
          border: '1px solid var(--line)',
          background: 'var(--surface)',
        }}
      >
        <span className="n-field__label">
          Macros — calculées depuis les ingrédients
        </span>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.4rem' }}
        >
          {MACRO_ROWS.map((row) => (
            <div key={row.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
              <span
                style={{
                  fontSize: '0.7rem',
                  color: 'var(--ink-soft)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <MacroDot macro={row.key} />
                {row.short}
              </span>
              <strong
                style={{
                  fontSize: '1rem',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--ink)',
                }}
              >
                {formatMacro(totals[row.key], row.unit)}
              </strong>
            </div>
          ))}
        </div>
        {yieldValue > 0 && (
          <span className="n-pick__meta tnum">
            {isPortions ? 'Par portion' : 'Pour 100 g'} : {formatMacro(perUnit.protein, 'g')} P ·{' '}
            {formatMacro(perUnit.carbs, 'g')} G · {formatMacro(perUnit.fat, 'g')} L ·{' '}
            {formatMacro(perUnit.kcal, 'kcal')} kcal
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <span className="n-field__label">Ingrédients</span>

        {draft.ingredients.length === 0 && !picking && (
          <Hint>
            Aucun ingrédient. Une recette sans ingrédient n’a aucune macro à calculer : elle ne
            pourrait pas être ajoutée au journal.
          </Hint>
        )}

        {draft.ingredients.map((item) => (
          <IngredientRow
            key={item.key}
            item={item}
            onPatch={(patch) => {
              onDraftChange({
                ...draft,
                ingredients: draft.ingredients.map((candidate) =>
                  candidate.key === item.key ? { ...candidate, ...patch } : candidate,
                ),
              });
            }}
            onRemove={() => {
              onDraftChange({
                ...draft,
                ingredients: draft.ingredients.filter((candidate) => candidate.key !== item.key),
              });
            }}
          />
        ))}

        {picking ? (
          <FoodPicker
            onCancel={() => {
              setPicking(false);
            }}
            onPick={(food) => {
              onDraftChange({
                ...draft,
                ingredients: [
                  ...draft.ingredients,
                  { key: newClientId(), food, quantity: '100', portionId: null },
                ],
              });
              setPicking(false);
            }}
          />
        ) : (
          <Button
            variant="ghost"
            shape="rounded"
            block
            onClick={() => {
              setPicking(true);
            }}
          >
            Ajouter un ingrédient
          </Button>
        )}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        <span className="n-field__label">
          Étapes de préparation — une par ligne
        </span>
        <textarea
          value={draft.steps}
          onChange={(event) => {
            onDraftChange({ ...draft, steps: event.target.value });
          }}
          rows={5}
          placeholder={'Faire revenir l’oignon\nAjouter les épices\nMijoter 20 minutes'}
          className="ui-textarea"
        />
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '44px' }}>
        <input
          type="checkbox"
          checked={draft.isFavorite}
          onChange={(event) => {
            onDraftChange({ ...draft, isFavorite: event.target.checked });
          }}
          style={{ width: '22px', height: '22px', accentColor: 'var(--brand)' }}
        />
        <span style={{ fontSize: '1rem' }}>Favori — proposé dans l’accès rapide</span>
      </label>

      <Button
        variant="primary"
        shape="rounded"
        block
        disabled={!isRecipeDraftValid(draft) || saving}
        onClick={onSubmit}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer la recette'}
      </Button>

      <Hint>
        Corriger une recette met à jour ses macros, puisqu’elles sont calculées depuis les
        ingrédients. Les entrées déjà enregistrées dans le journal, elles, ne bougent pas : elles
        portent leur propre copie des lignes et des macros, figée à la saisie.
      </Hint>
    </Sheet>
  );
}
